import React, { useEffect, useMemo } from 'react';
import EventsSelect from '@src/emp-3.0/components/EventsSelect';
import CustomFormItem from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/CustomFormItem';
import { EventLabel } from '@src/emp-3.0/components/Metrics/components';
import EventItem from './EventItem';
import { EEventType } from '@src/emp-3.0/types';
import { IFlowEventsProps } from './typed';
import { useFlowStore } from '@src/pages/workflow/store/flowBuilder';
import { Form } from 'antd';
import { cloneDeep } from 'lodash';

const FlowErrorEvents: React.FC<IFlowEventsProps> = ({ form, isView }) => {
  const successEvent = Form.useWatch(['definition', 'events', 'success_event'], form);
  const startEvent = Form.useWatch(['definition', 'events', 'start_event'], form);
  const flowFormErrorEventIds = Form.useWatch(['definition', 'events', 'error_event'], form);

  useEffect(() => {
    if (flowFormErrorEventIds) {
      setErrorEvents(flowFormErrorEventIds);
    }
  }, [flowFormErrorEventIds]);

  const { errorEvents = [], setErrorEvents } = useFlowStore();

  const onEventsChange = (value) => {
    // error event is multiple
    if (value.length === 0) {
      // do nothing, user can remove events by clicking on remove button
      return;
    }
    form.setFieldsValue({
      definition: { events: { error_event: value } },
    });
  };

  const defaultSelectedValue = useMemo(() => {
    return errorEvents?.map((event) => (typeof event !== 'object' ? event : event.event_id));
  }, [errorEvents]);

  return (
    <>
      <CustomFormItem
        name={['definition', 'events', 'error_event']}
        dependencies={[
          ['definition', 'events', 'success_event'],
          ['definition', 'events', 'start_event'],
          ['definition', 'events', 'without_event'],
        ]}
        label={
          <EventLabel
            prefix={null}
            tooltip="Error event defines the type of error code and messages displayed for flow error analysis"
            text="Error Event"
          />
        }
        labelAlign="left"
        labelCol={{ span: 6 }}
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              const startEvent = getFieldValue(['definition', 'events', 'start_event']);
              const successEvent = getFieldValue(['definition', 'events', 'success_event']);

              const hasDuplicate = value.includes(startEvent) || value.includes(successEvent);

              if (!value || !hasDuplicate) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error(
                  'Error Event cannot be the same as Initial Event or Complete Event Or Without Event',
                ),
              );
            },
          }),
        ]}
        style={{ marginBottom: 12 }}
      >
        {isView ? (
          <div />
        ) : (
          <EventsSelect
            eventType={EEventType.ERROR_EVENT}
            mode="multiple"
            onChange={(value, events) => {
              onEventsChange(value, events);
            }}
            disabledOptions={[startEvent, successEvent]}
            defaultSelectedValue={defaultSelectedValue}
          />
        )}
      </CustomFormItem>
      {errorEvents?.map((event, index) => {
        return (
          <EventItem
            style={{ marginLeft: '28px' }}
            eventId={typeof event !== 'object' ? event : undefined}
            event={typeof event === 'object' ? event : undefined}
            key={`error_events_${index}`}
            type={EEventType.ERROR_EVENT}
            viewModeProps={{
              onEdit: (event) => {
                const newErrorEvents = cloneDeep(errorEvents);
                newErrorEvents[index] = event;
                setErrorEvents(newErrorEvents);
              },
              onDelete: (event) => {
                const newErrorEvents = [...errorEvents];
                newErrorEvents.splice(index, 1);
                setErrorEvents(newErrorEvents);
                form.setFieldsValue({
                  definition: { events: { error_event: newErrorEvents } },
                });
                form.validateFields([
                  ['definition', 'events', 'start_event'],
                  ['definition', 'events', 'success_event'],
                  ['definition', 'events', 'error_event'],
                  ['definition', 'events', 'without_event'],
                ]);
              },
              onClone: (event) => {
                const newErrorEvents = [...errorEvents];
                newErrorEvents.splice(index, 0, event);
                setErrorEvents(newErrorEvents);
              },
            }}
            editModeProps={{
              onSave: (event) => {
                const newErrorEvents = cloneDeep(errorEvents);
                if (event.event_id) {
                  newErrorEvents[index] = event.event_id;
                } else {
                  // remove event
                  newErrorEvents.splice(index, 1);
                }
                if (!newErrorEvents.some((e) => typeof e === 'object')) {
                  // set events to form
                  form.setFieldsValue({
                    definition: { events: { error_event: newErrorEvents } },
                  });
                }
                setTimeout(() => {
                  setErrorEvents(newErrorEvents);
                }, 100);
              },
              onCancel: (event) => {
                const newErrorEvents = cloneDeep(errorEvents);
                // from clone, event.event_id is null, remove it
                if (event.event_id === null) {
                  newErrorEvents.splice(index, 1);
                } else if (event.event_id || event.related_flows) {
                  // for mapped event event_id is exist, for raw event related_flows is exist
                  newErrorEvents[index] = event.event_id || event.event_name;
                } else {
                  // from apply rules
                  // remove event
                  newErrorEvents.splice(index, 1);
                }
                setErrorEvents(newErrorEvents);
              },
              onChange: (event) => {
                const newErrorEvents = cloneDeep(errorEvents);
                newErrorEvents[index] = event;
                const distinctEvents = Array.from(new Set(newErrorEvents));
                setErrorEvents(distinctEvents);
                if (!newErrorEvents.some((e) => typeof e === 'object')) {
                  // set events to form
                  form.setFieldsValue({
                    definition: { events: { error_event: distinctEvents } },
                  });
                  form.validateFields([
                    ['definition', 'events', 'start_event'],
                    ['definition', 'events', 'success_event'],
                    ['definition', 'events', 'error_event'],
                    ['definition', 'events', 'without_event'],
                  ]);
                }
              },
            }}
          />
        );
      })}
      {errorEvents.length > 0 && <div style={{ height: '34px' }}></div>}
    </>
  );
};

export default FlowErrorEvents;
